import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [referralStatus, setReferralStatus] = useState(null); // null | 'valid' | 'invalid'
  const [referralOwner, setReferralOwner] = useState("");

  // Validate the referral code as user types (debounced via useEffect)
  useEffect(() => {
    const code = referralCode.trim().toUpperCase();
    if (!code) {
      setReferralStatus(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/referrals/validate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });
        const d = await r.json();
        if (d.valid) {
          setReferralStatus("valid");
          setReferralOwner(d.owner_name || "");
        } else {
          setReferralStatus("invalid");
        }
      } catch {
        setReferralStatus(null);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [referralCode]);

  const getAge = (dob) => {
    if (!dob) return null;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords don't match!");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!dateOfBirth) {
      toast.error("Date of birth is required");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, name, date_of_birth: dateOfBirth }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Registration failed");
      }

      login(data.user);

      // Redeem referral code if user provided one
      const code = referralCode.trim().toUpperCase();
      if (code && referralStatus === "valid") {
        try {
          const r = await fetch(`${API}/referrals/redeem`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
          });
          if (r.ok) {
            const d = await r.json();
            toast.success(`🎁 +${d.referee_reward_days} days of OurSpacez Gold unlocked!`);
          }
        } catch (e) {
          // Non-fatal — they're registered, just no referral bonus
        }
      }

      toast.success("Welcome to OurSpacez! Verify your ID to continue.");
      navigate("/kyc");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-[#FF66FF] hover:text-[#00FFFF] mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to OurSpacez
        </button>

        <div className="myspace-card">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold mb-2">
              <span className="glitter-text">Our</span>
              <span className="text-[#00FFFF]">Space</span>
            </h1>
            <p className="text-[#99CCFF]">Sign Up - It's FREE!</p>
            <motion.p
              className="text-[#FFFF00] text-sm mt-2"
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              ★ Join millions of friends ★
            </motion.p>
          </div>

          {/* Google Sign Up */}
          <button
            onClick={handleGoogleLogin}
            className="myspace-btn w-full mb-4 flex items-center justify-center gap-3"
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign Up with Google
          </button>

          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 h-px bg-[#6600CC]"></div>
            <span className="text-[#99CCFF] text-sm">or</span>
            <div className="flex-1 h-px bg-[#6600CC]"></div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#FF66FF] text-sm font-bold">
                Display Name
              </Label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="myspace-input mt-1"
                placeholder="xXYourNameXx"
                required
                data-testid="name-input"
              />
              <p className="text-[#6699CC] text-xs mt-1">This is how you'll appear to friends</p>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#FF66FF] text-sm font-bold">
                Email Address
              </Label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="myspace-input mt-1"
                placeholder="you@example.com"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="dob" className="text-[#FF66FF] text-sm font-bold">
                Date of Birth
              </Label>
              <input
                id="dob"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="myspace-input mt-1"
                required
                max={new Date().toISOString().split("T")[0]}
                data-testid="dob-input"
              />
              {dateOfBirth && getAge(dateOfBirth) !== null && (
                <p className="text-xs mt-1" style={{ color: getAge(dateOfBirth) < 18 ? "#FF3366" : "#00FF00" }}>
                  {getAge(dateOfBirth) < 18
                    ? "🔞 OurSpacez is strictly 18+. Registration will be denied."
                    : `Age ${getAge(dateOfBirth)} ✓`}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-[#FF66FF] text-sm font-bold">
                Password
              </Label>
              <div className="relative mt-1">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="myspace-input pr-12"
                  placeholder="Min 6 characters"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FF66FF]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-[#FF66FF] text-sm font-bold">
                Confirm Password
              </Label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="myspace-input mt-1"
                placeholder="Type password again"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <div>
              <Label htmlFor="ref" className="text-[#FF66FF] text-sm font-bold flex items-center gap-2">
                🎁 Referral code (optional)
              </Label>
              <Input
                id="ref"
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className="myspace-input mt-1 font-mono"
                placeholder="OS-FRIEND-XXXXX"
                data-testid="referral-code-input"
              />
              {referralStatus === "valid" && (
                <p className="text-xs mt-1 text-[#00FF00]" data-testid="ref-valid">
                  ✓ Valid code from {referralOwner || "a friend"} — you'll get 30 days of Gold free!
                </p>
              )}
              {referralStatus === "invalid" && (
                <p className="text-xs mt-1 text-[#FF6699]" data-testid="ref-invalid">
                  ✗ Invalid or fully-redeemed code
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="myspace-btn-primary w-full disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {loading ? "Creating account..." : "★ JOIN OURSPACEZ ★"}
            </button>
          </form>

          <p className="text-center mt-6 text-[#99CCFF] text-sm">
            Already a member?{" "}
            <Link to="/login" className="text-[#FF66FF] hover:text-[#00FFFF]" data-testid="login-link">
              Log In
            </Link>
          </p>

          <p className="text-center mt-4 text-[#6699CC] text-xs">
            By signing up, you agree to our{" "}
            <a href="#" className="text-[#FF66FF]">Terms</a> and{" "}
            <a href="#" className="text-[#FF66FF]">Privacy Policy</a>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
